import React, { useState, useEffect, useRef } from "react";
import { 
  Box, 
  Button, 
  Drawer, 
  Typography, 
  List, 
  ListItem, 
  ListItemText,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  IconButton,
  Divider
} from "@material-ui/core";
import { Settings, Close } from "@material-ui/icons";
import { useSelector } from "../../../../store";
import * as webRTCHandler from "../../../../utils/webRTCHandler";
import { getIOSVersion } from "../../../../utils/iosVersionGetter";

const SettingsButton = () => {
  const [open, setOpen] = useState(false);
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState("");
  const [selectedVideoDevice, setSelectedVideoDevice] = useState("");
  const { localStream, platform } = useSelector((state) => state.room);
  const isAndroid = platform === "android";
  const isIOS = platform === "ios";

  const lastMicPickRef = useRef({ id: "", ts: 0 });

  const pickMic = async (deviceId) => {
    const now = Date.now();
    const last = lastMicPickRef.current;
  
    // prevent double-run (touch + click etc.)
    if (last.id === deviceId && now - last.ts < 800) return;
  
    lastMicPickRef.current = { id: deviceId, ts: now };
  
    // update UI immediately (so selection changes even if gUM is slow)
    setSelectedAudioDevice(deviceId);
  
    await handleAudioDeviceChange(deviceId);
  };


  useEffect(() => {
    if (open) {
      getDevices();
    }
  }, [open]);

  useEffect(() => {
    // Add device change listener
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getDevices);
    };
  }, []);

  // Sync selected devices when localStream changes (e.g., when browser auto-switches to Bluetooth)
  useEffect(() => {
    if (localStream && open) {
      const currentAudioTrack = localStream.getAudioTracks()[0];
      const currentVideoTrack = localStream.getVideoTracks()[0];

      if (currentAudioTrack) {
        const currentDeviceId = currentAudioTrack.getSettings().deviceId;
        if (currentDeviceId) {
          setSelectedAudioDevice(currentDeviceId);
        }
      }
      if (currentVideoTrack) {
        const currentDeviceId = currentVideoTrack.getSettings().deviceId;
        if (currentDeviceId) {
          setSelectedVideoDevice(currentDeviceId);
        }
      }
    }
  }, [localStream, open]);

  const getDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      const videoInputs = devices.filter(device => device.kind === 'videoinput');
      
      setAudioDevices(audioInputs);
      setVideoDevices(videoInputs);

      // Always sync selected devices with current active tracks
      if (localStream) {
        const currentAudioTrack = localStream.getAudioTracks()[0];
        const currentVideoTrack = localStream.getVideoTracks()[0];

        if (currentAudioTrack) {
          const currentDeviceId = currentAudioTrack.getSettings().deviceId;
          // Update if deviceId exists and is different from current selection
          if (currentDeviceId) {
            setSelectedAudioDevice(currentDeviceId);
          }
        }
        if (currentVideoTrack) {
          const currentDeviceId = currentVideoTrack.getSettings().deviceId;
          // Update if deviceId exists and is different from current selection
          if (currentDeviceId) {
            setSelectedVideoDevice(currentDeviceId);
          }
        }
      }
    } catch (error) {
      console.error('Error getting devices:', error);
    }
  };

  const handleAudioDeviceChange = async (deviceId) => {
    try {
      const wasMuted = localStream?.getAudioTracks()[0] && !localStream.getAudioTracks()[0].enabled;
  
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
        video: false
      });
  
      if (localStream) {
        const newAudioTrack = newStream.getAudioTracks()[0];
        if (wasMuted) {
          newAudioTrack.enabled = false;
        }
        await webRTCHandler.switchAudioTrack(newAudioTrack);
        setSelectedAudioDevice(deviceId);
      }
    } catch (error) {
      console.error('Error switching microphone:', error);
    }
  };

  const handleVideoDeviceChange = async (deviceId) => {
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { deviceId: { exact: deviceId } }
      });

      if (localStream) {
        const newVideoTrack = newStream.getVideoTracks()[0];
        
        // Update the track in WebRTC connections first
        await webRTCHandler.switchVideoTrack(newVideoTrack);
        setSelectedVideoDevice(deviceId);
      }
    } catch (error) {
      console.error('Error switching camera:', error);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  const handleCloseTouchEnd = (e) => {
    e.preventDefault();
    setOpen(false);
  };

  return (
    <Box>
      <Button
        onClick={() => setOpen(true)}
        onTouchEnd={handleTouchEnd}
        sx={{
          color: "white",
          boxShadow: "none",
        }}
        variant="contained"
      >
        <Settings />
      </Button>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{
          onBackdropClick: () => setOpen(false),
        }}
      >
        <Box sx={{ width: 350, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Settings</Typography>
            <IconButton onClick={() => setOpen(false)} onTouchEnd={handleCloseTouchEnd}>
              <Close />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <List>
            <ListItem>
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Microphone</Typography>
                
                {/* Radio buttons for mobile */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={selectedAudioDevice}
                      onChange={(e) => {
                        if (!isIOS) handleAudioDeviceChange(e.target.value);
                      }}
                    >
                      {audioDevices.map((device) => (
                        <FormControlLabel
                          key={device.deviceId}
                          value={device.deviceId}
                          control={<Radio />}
                          label={device.label || `Microphone ${audioDevices.indexOf(device) + 1}`}
                          {...(isIOS
                            ? {
                                onTouchEnd: (e) => {
                                  e.stopPropagation();
                                  pickMic(device.deviceId);
                                },
                                onClick: () => pickMic(device.deviceId), // fallback if touchend not delivered
                              }
                            : {})}
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* Dropdown for desktop */}
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <FormControl fullWidth>
                    <Select
                      value={selectedAudioDevice}
                      onChange={(e) => handleAudioDeviceChange(e.target.value)}
                      variant="outlined"
                      size="small"
                      {...(isIOS
                        ? {
                            MenuProps: {
                              disablePortal: true,
                              keepMounted: true,
                            },
                          }
                        : {})}
                    >
                      {audioDevices.map((device) => (
                        <MenuItem 
                          key={device.deviceId} 
                          value={device.deviceId}
                        >
                          {device.label || `Microphone ${audioDevices.indexOf(device) + 1}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </ListItem>

            <ListItem sx={{ display: { xs: 'none', sm: 'none', md: 'flex' } }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Camera</Typography>
                <FormControl fullWidth>
                  <Select
                    value={selectedVideoDevice}
                    onChange={(e) => handleVideoDeviceChange(e.target.value)}
                    variant="outlined"
                    size="small"
                  >
                    {videoDevices.map((device) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Camera ${videoDevices.indexOf(device) + 1}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SettingsButton;


